import { shortcut } from '../ui/responsive';
import { hexA } from '@scribbleplot/shared';
import { color, font } from '../tokens';
import { Button } from '../ui/primitives';

/** Zoom controls and the shortcuts toggle, bottom-right of the canvas. */
export function ZoomBar({
  scale,
  accent,
  shortcutsOpen,
  onZoomIn,
  onZoomOut,
  onFit,
  onToggleShortcuts,
}: {
  scale: number;
  accent: string;
  shortcutsOpen: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  onToggleShortcuts: () => void;
}) {
  const iconButton = {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: color.icon,
  } as const;

  return (
    <div
      className="sp-zoom-bar"
      style={{
        position: 'absolute',
        right: 12,
        bottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: 4,
        background: color.panel,
        border: `1px solid ${color.lineSoft}`,
        borderRadius: 11,
        zIndex: 50,
      }}
    >
      <Button
        title="Zoom out — ⌘−"
        onClick={onZoomOut}
        hoverStyle={{ background: color.hoverStrong }}
        style={iconButton}
      >
        <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
          <path d="M3 7h8" style={{ stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' }} />
        </svg>
      </Button>

      <div
        aria-live="polite"
        style={{ minWidth: 44, textAlign: 'center', fontSize: 11, fontFamily: font.mono, color: '#B9BCC7' }}
      >
        {Math.round(scale * 100)}%
      </div>

      <Button title="Zoom in — ⌘+" onClick={onZoomIn} hoverStyle={{ background: color.hoverStrong }} style={iconButton}>
        <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
          <path d="M3 7h8M7 3v8" style={{ stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' }} />
        </svg>
      </Button>

      <Button
        title="Zoom to fit — ⌘1"
        onClick={onFit}
        hoverStyle={{ background: color.hoverStrong }}
        style={iconButton}
      >
        <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
          <path
            d="M2.5 5V2.5H5M11.5 9V11.5H9M2.5 9v2.5H5M11.5 5V2.5H9"
            style={{
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 1.5,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          />
        </svg>
      </Button>

      <div aria-hidden style={{ width: 1, height: 18, background: color.lineSoft, margin: '0 2px' }} />

      <Button
        title="Keyboard shortcuts — ?"
        onClick={onToggleShortcuts}
        ariaPressed={shortcutsOpen}
        hoverStyle={{ background: color.hoverStrong }}
        style={{
          ...iconButton,
          background: shortcutsOpen ? hexA(accent, 0.15) : 'transparent',
          color: shortcutsOpen ? accent : color.icon,
        }}
      >
        <span style={{ fontFamily: font.mono, fontSize: 12 }}>?</span>
      </Button>
    </div>
  );
}

/** Grouped so the keyboard-navigation block reads as its own idea. */
const SHORTCUTS: [string, string][] = [
  ['V', 'Select'],
  ['H / Space', 'Pan'],
  ['N', 'Sticky note'],
  ['T', 'Text'],
  ['F', 'Area'],
  ['D', 'Draw'],
  ['C', 'Connector'],
  ['L', 'Link card'],
  ['M', 'Comment'],
  ['⌘G', 'Group'],
  ['⇧⌘G', 'Ungroup'],
  ['⌘D', 'Duplicate'],
  ['⌘Z', 'Undo'],
  ['⇧⌘Z', 'Redo'],
  ['⌫', 'Delete'],
  ['⌘1', 'Zoom to fit'],
  ['Tab', 'Next object'],
  ['⇧Tab', 'Previous object'],
  ['↑ ↓ ← →', 'Move selection'],
  ['⇧ + arrows', 'Move further'],
  ['Enter', 'Edit / place'],
  ['Esc', 'Deselect / release Tab'],
];

export function ShortcutsPanel({ minimapVisible, onShowGuide }: { minimapVisible: boolean; onShowGuide?: () => void }) {
  return (
    <div
      className="sp-shortcuts"
      style={{
        position: 'absolute',
        right: 12,
        bottom: minimapVisible ? 176 : 56,
        width: 212,
        maxHeight: minimapVisible ? 'calc(100% - 190px)' : 'calc(100% - 70px)',
        overflowY: 'auto',
        background: color.panel,
        border: `1px solid ${color.lineSoft}`,
        borderRadius: 12,
        padding: '11px 14px 12px',
        zIndex: 55,
        boxShadow: '0 10px 30px rgba(0,0,0,.45)',
        animation: 'sp-pop-in .15s ease',
      }}
    >
      <div
        style={{ fontSize: 10, fontFamily: font.mono, letterSpacing: '.12em', color: color.textFaint, marginBottom: 7 }}
      >
        SHORTCUTS
      </div>
      {onShowGuide && (
        <button className="sp-button" style={{ marginBottom: 12, width: '100%' }} onClick={onShowGuide}>
          Getting started
        </button>
      )}
      {SHORTCUTS.map(([key, label]) => (
        <div
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 18,
            padding: '2.5px 0',
          }}
        >
          <div style={{ fontSize: 12, color: '#B9BCC7' }}>{label}</div>
          <div
            style={{
              fontFamily: font.mono,
              fontSize: 10.5,
              color: color.textMuted,
              background: color.raisedAlt,
              border: `1px solid rgba(255,255,255,.1)`,
              borderRadius: 5,
              padding: '1px 6px',
              whiteSpace: 'nowrap',
            }}
          >
            {shortcut(key)}
          </div>
        </div>
      ))}
    </div>
  );
}
